<template>
  <section class="service-block" :class="variant ? 'is-' + variant : ''" :id="blockId">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <section-title
            v-if="!hasAside && hasTitle"
            data-aos="fade-up"
            :kicker="kicker"
            :title="title"
            :lede="lede"
            align="left"
            :tone="variant === 'dark' ? 'dark' : 'light'"
          />
          <div class="service-card" :class="variant ? 'is-' + variant : ''">
            <div v-if="hasAside" class="service-split">
              <div class="service-main" data-aos="fade-right">
                <!-- With an aside the title sits in the copy column, so the
                     aside top-aligns with the kicker like the web design brief. -->
                <section-title
                  v-if="hasTitle"
                  :kicker="kicker"
                  :title="title"
                  :lede="lede"
                  align="left"
                  :tone="variant === 'dark' ? 'dark' : 'light'"
                />
                <slot />
              </div>
              <panel-list
                :title="asideTitle"
                :items="asideItems"
                :tone="variant === 'dark' ? 'dark' : 'light'"
              />
            </div>
            <!-- Without an aside the page's own copy is the card, so it keeps
                 the same copy wrapper the split uses. That is what lets the
                 card-copy rules below target one element instead of every
                 descendant, which is what used to reach into the aside panel. -->
            <div v-else class="service-main" data-aos="fade-up">
              <slot />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.service-block {
  background: $ground-tint;

  &.is-plain {
    background: $ground;
  }
}

.service-card {
  background: $ground;
  border: 1px solid $line;
  border-radius: $r-lg;
  color: $text-body;
  line-height: 1.75;
  padding: 2.5rem;

  &.is-plain {
    background: transparent;
    border: 0;
    border-radius: 0;
    color: inherit;
    line-height: inherit;
    padding: 0;
  }

  /* Card copy belongs to the copy column only. These rules used to hang off the
     whole card, which meant they reached into the aside panel and restyled its
     rows and drew a dash marker on each; the aside had to cancel them back.
     Targeting the copy wrapper removes that fight. */
  &:not(.is-plain) .service-main {
    :deep(h2),
    :deep(h3) {
      color: $text-heading;
      font-family: $headings-font-family;
      margin-bottom: 0.85rem;
    }

    :deep(h2) {
      font-size: 1.35rem;
      margin-top: 2rem;

      &:first-child {
        margin-top: 0;
      }
    }

    :deep(> p:first-of-type) {
      color: $text-heading;
      font-size: 1.2rem;
      font-weight: 500;
      letter-spacing: -0.01em;
      line-height: 1.6;
      max-width: 48rem;
    }

    :deep(p) {
      margin-bottom: 1.25rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(ul) {
      display: grid;
      gap: 0.85rem 2.5rem;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      margin: 0 0 1.5rem;
      padding: 0;

      @media (max-width: 767.98px) {
        grid-template-columns: 1fr;
      }
    }

    :deep(li) {
      list-style: none;
      margin: 0;
      padding-left: 1.35rem;
      position: relative;

      &::before {
        background: $text-heading;
        content: '';
        height: 1px;
        left: 0;
        position: absolute;
        top: 0.85em;
        width: 0.7rem;
      }
    }
  }

  :deep(a:not(.btn):not(.app-btn)) {
    color: $primary;
  }
}

/* Optional two-column intro: copy on the left, an included-points aside on the
   right. Same anatomy as the web design brief, so service intros match. */
.service-split {
  display: grid;
  gap: 2.25rem;
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 992px) {
    align-items: start;
    gap: $split-gap;
    grid-template-columns: $split;
  }
}

.service-main {
  max-width: 40rem;

  /* The aside panel on the right is the taller column. Let the copy centre
     against it rather than pinning to the top, so the two sides read level.
     Where the copy is the taller column it fills the row and this does
     nothing, which keeps the panel top aligned with the block title. */
  @media (min-width: 992px) {
    align-self: center;
  }
}

/* The aside panel itself is PanelList: the surface, the hairline beam, the row
   rhythm, the 2.4rem mark and the hover response are declared once there and
   shared with the promo brief, the why grid and the quote band. This component
   used to carry its own copy of all of it, and the pages that passed an #aside
   slot carried a third. */

/* Dark variant. Uses the same palette as the site's other dark sections
   (section.dark, .why-grid.dark, .quote-band.dark) so a dark block drops into
   any page without inventing a second dark look: ground-dark, white headings,
   white-72% copy, and the translucent panel those sections already use.
   Declared last, and with the full ancestor chain, so it wins over the light
   card rules above without needing !important. */
.service-block.is-dark {
  background: $ground-dark;
  color: $on-dark-body;

  .service-card:not(.is-plain) {
    background: $on-dark-fill;
    border-color: $on-dark-line;
    color: $on-dark-body;

    :deep(h2),
    :deep(h3) {
      color: $on-dark-strong;
    }

    :deep(> p:first-of-type) {
      color: $on-dark-strong;
    }

    :deep(li::before) {
      background: $on-dark-line-strong;
    }
  }
}
</style>

<script>
import SectionTitle from '@/Components/SectionTitle.vue';
import PanelList from '@/Components/PanelList.vue';

export default {
  components: {
    PanelList,
    SectionTitle
  },

  props: {
    title: {
      type: String,
      default: ''
    },
    kicker: {
      type: String,
      default: ''
    },
    lede: {
      type: String,
      default: ''
    },
    blockId: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: ''
    },
    /* The included-points panel. Passed as data rather than as an #aside slot
       so the panel's markup and its styles are owned in one place. */
    asideTitle: {
      type: String,
      default: ''
    },
    asideItems: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    hasAside() {
      return this.asideItems.length > 0;
    },

    hasTitle() {
      return !!(this.kicker || this.title || this.lede);
    }
  }
};
</script>
